import { usePrevious } from '@calendly/hooks'
import { Logger } from '@calendly/logging-service'
import { useCallback, useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { useGetUserQuery } from 'common'
import { eventService } from 'services/analytics/event_service'

import { TRACKING_EVENT_NAMES, DEFAULT_ENTITIES, DEFAULT_VERSION } from './constants'

const LITE_IDENTITY_PATH_PREFIX = '/app/lite/'

const isLiteIdentityHost = () => (
  typeof window !== 'undefined'
  && window.location.pathname.startsWith(LITE_IDENTITY_PATH_PREFIX)
)

/**
 * Dispatches a tracking event with standardized validation and formatting.
 * Use this function in utility/API files where React hooks are not available.
 * @param {Object} config - The tracking event configuration
 * @param {string} config.name - The name of the event from TRACKING_EVENT_NAMES
 * @param {number} [config.version=DEFAULT_VERSION] - Version of the event schema
 * @param {Object} [config.dependencies] - Entity identifiers and relationships
 * @param {Array<string>} [config.entities=DEFAULT_ENTITIES] - Related entities for this event
 * @param {Object} [config.properties={}] - Event-specific properties and metadata
 * @param {boolean} [config.isExternal=false] - Whether this is an external event
 */
export const trackEvent = ({
  name,
  version = DEFAULT_VERSION,
  dependencies = {},
  entities = DEFAULT_ENTITIES,
  properties = {},
  isExternal = false,
}) => {
  try {
    // TODO: This will need to be deleted with some kind of fix to properly track lite account analytic events.
    if (isLiteIdentityHost()) {
      Logger.warn('Analytics event skipped:', {
        eventName: name,
        eventProperties: properties,
        reason: 'Lite identity host cannot reach /api/analytics/track',
      })
      return
    }

    // Error handling
    if (!dependencies.user_uuid && !dependencies.user_id) {
      throw new Error('No user context available')
    }

    const isValidEventName = Object.values(TRACKING_EVENT_NAMES).includes(name)
    if (!isValidEventName) {
      throw new Error(`Event name "${name}" is not in TRACKING_EVENT_NAMES`)
    }

    version = typeof version !== 'number' ? Number(version) : version
    if (Number.isNaN(version)) {
      throw new Error('Event version must be a valid number')
    }

    // Dispatch valid event
    eventService.dispatch({
      name,
      version,
      dependencies,
      entities,
      properties,
      isExternal,
    })
  } catch (error) {
    Logger.warn('Analytics event skipped:', {
      eventName: name,
      eventProperties: properties,
      reason: error.message
    })
  }
}

/**
 * React hook that provides a standardized tracking function for components.
 * Automatically includes current user context and validates event names.
 * @returns {Function} A memoized tracking function with the following signature:
 *   @param {Object} config - The tracking event configuration
 *   @param {string} config.name - The name of the event from TRACKING_EVENT_NAMES
 *   @param {number} [config.version=DEFAULT_VERSION] - Version of the event schema
 *   @param {Object} [config.dependencies] - Entity identifiers and relationships
 *   @param {Array<string>} [config.entities=DEFAULT_ENTITIES] - Related entities for this event
 *   @param {Object} [config.properties={}] - Event-specific properties and metadata
 *   @param {boolean} [config.isExternal=false] - Whether this is an external event
 */
export const useTrack = () => {
  const { data: user } = useGetUserQuery()

  return useCallback((config) => {
    trackEvent({
      ...config,
      dependencies: {
        ...config.dependencies,
        user_uuid: user?.uuid,
        user_id: user?.id
      }
    })
  }, [user?.uuid, user?.id])
}

/**
 * React hook that tracks an event only once when a condition becomes ready.
 * Useful for tracking events that should only fire once per component lifecycle.
 * @param {boolean} ready - Whether the tracking condition is ready
 * @param {Object} config - The tracking event configuration (same as trackEvent)
 */
export const useTrackOnce = (ready, config) => {
  const hasTracked = useRef(false)
  const track = useTrack()

  useEffect(() => {
    if (ready) {
      if (!hasTracked.current) {
        track(config)
        hasTracked.current = true
      }
    } else {
      hasTracked.current = false
    }
  }, [ready, config, track])
}

/**
 * React hook that tracks an event on navigation when conditions are met.
 * Fires every time the user navigates to a page where the tracking condition is ready,
 * including repeat visits to the same route.
 * @param {boolean} ready - Whether the tracking condition is ready
 * @param {Object} config - The tracking event configuration (same as trackEvent)
 */
export const useTrackOnNavigation = (ready, config) => {
  const { pathname } = useLocation()
  const previousPathname = usePrevious(pathname)
  const hasTrackedForCurrentNavigation = useRef(false)
  const track = useTrack()

  // Reset tracking state when route changes
  useEffect(() => {
    if (previousPathname && previousPathname !== pathname) {
      hasTrackedForCurrentNavigation.current = false
    }
  }, [pathname, previousPathname])

  // Track when conditions become ready (either on route change or async data load)
  useEffect(() => {
    if (ready && !hasTrackedForCurrentNavigation.current) {
      track(config)
      hasTrackedForCurrentNavigation.current = true
    }
  }, [ready, config, track])
}
